import { flattenDeep } from 'lodash-es';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { type GridComponents, type GridListProps, VirtuosoGrid } from 'react-virtuoso';
import MediaPreviewContent from '@/components/common/media-content/media-preview-content';
import TabList from '@/components/common/tablist';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useMediaMessages, {
  type MediaMessageFilterType,
  mediaFilterOptionsSchema,
} from '@/hooks/conversations/use-media-messages';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import cn from '@/utils/merge-class-names';

interface Props {
  type: ConversationType;
}

const gridComponents: GridComponents = {
  List: forwardRef<HTMLDivElement, GridListProps>(({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      className={cn('grid grid-cols-3 auto-rows-fr gap-2 mx-5', className)}>
      {children}
    </div>
  )),
  Item: ({ children, className, ...props }) => (
    <div
      {...props}
      className={cn('break-all overflow-hidden aspect-square', className)}>
      {children}
    </div>
  ),
  Footer: () => <div className='pb-safe-or-4' />,
};

export default function MediaMessagesIndexRoute({ type }: Props) {
  useScrollRestoration();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.MEDIA || ROUTES.MESSAGES.GROUPS.MEDIA);

  const hash = mediaFilterOptionsSchema.safeParse(new URL(window.location.href).hash.replace('#', ''));

  const { messages, filteredBy, setFilter } = useMediaMessages(undefined, 'backward', [hash.data ?? 'all']);

  const handleChangeFilter = (filterType: MediaMessageFilterType) => {
    setFilter([filterType]);

    const href = (type === 'session' ? ROUTES.MESSAGES.SESSIONS.MEDIA : ROUTES.MESSAGES.GROUPS.MEDIA).$buildPath({
      params: { id },
      hash: filterType === 'all' ? undefined : filterType,
    });
    if (BUILD_EMBED_TYPE !== 'embed-native') {
      history.replaceState({ ...history.state }, '', href);
    } else {
      navigate(href, { replace: true });
    }
  };

  return (
    <PageLayout
      headerConfig={{
        navigateBack: true,
        elementAfterNav: (
          <TabList
            tabs={filterTabs}
            activeKey={hash.data ?? 'all'}
            defaultActiveTab={hash.data ?? 'all'}
            onClickTab={handleChangeFilter}
            className='bg-white mt-2'
          />
        ),
      }}
      title={t('my-collection.media')}>
      <VirtuosoGrid
        data={messages}
        className='flex flex-1'
        components={gridComponents}
        itemContent={(_, m) => (
          <Link
            to={(type === 'session'
              ? ROUTES.MESSAGES.SESSIONS.MEDIA.$.BY_ID
              : ROUTES.MESSAGES.GROUPS.MEDIA.$.BY_ID
            ).$buildPath({
              relative: true,
              params: {
                messageId: m.ID,
              },
              searchParams: { filterTypes: flattenDeep([filteredBy]) },
            })}
            state={{ from: 'index' }}>
            <MediaPreviewContent
              type={m.MsgType}
              src={m.Message}
              className='desktop:w-[10.5rem] desktop:h-[10.5rem]'
            />
          </Link>
        )}
      />
    </PageLayout>
  );
}

const filterTabs: Array<{ key: MediaMessageFilterType; label: string }> = [
  {
    key: 'all',
    label: 'conversation.media.filters.all',
  },
  {
    key: 'images',
    label: 'conversation.media.filters.images',
  },
  {
    key: 'videos',
    label: 'conversation.media.filters.videos',
  },
  {
    key: 'files',
    label: 'conversation.media.filters.files',
  },
];
